import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from 'src/services/appconfig.service';
import { AppSettingsService } from 'src/services/appsettings.service';
import { AppService} from 'src/services/app.service';
import { NgIf, NgClass } from '@angular/common';

@Component({
    selector: 'app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
    standalone: true,
    imports: [NgIf, NgClass]
})
export class FeedbackComponent implements OnInit, OnDestroy {
  @Output() public feedbackSubmitted: EventEmitter<void> = new EventEmitter();

  public isFeedbackFormVisible: boolean = false;
  public currentRating: number = 0;
  public feedbackMessage: string = ''; 
  public fadeOut: boolean = false;
  public apiUrl: string = ''; 
  public messageType: 'success' | 'error' = 'success';
  public username: string = '';

  private handleClickOutsideBound: any;

  constructor(
    private appService: AppService,
    private appConfigService: AppConfigService,
    private appSettingsService: AppSettingsService,
    private elementRef: ElementRef
  ) {}

  // Initializes component and binds click event
  public async ngOnInit(): Promise<void> {
    await this.getUserInfo();
    this.handleClickOutsideBound = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.handleClickOutsideBound);
  }

  // Cleans up event listeners on destroy
  public ngOnDestroy(): void {
    document.removeEventListener('click', this.handleClickOutsideBound);
  }

  // Retrieves the logged-in username from settings
  public async getUserInfo(): Promise<void> {
    this.username = (await this.appSettingsService.getSettings())?.user?.username || '';
  }

  // Toggles the feedback form visibility
  public toggleFeedbackForm(): void {
    this.isFeedbackFormVisible = !this.isFeedbackFormVisible;
  }

  // Closes feedback form if clicked outside
  public handleClickOutside(event: Event): void {
    if (!this.isFeedbackFormVisible || !this.elementRef.nativeElement) return;

    const isClickOutside = !this.elementRef.nativeElement.contains(event.target as Node);
    if (isClickOutside) {
      this.isFeedbackFormVisible = false;
    }
  }

  // Sends user feedback to the backend
  public async sendFeedback(feedbackValue: string): Promise<void> {
    if (this.currentRating > 0 && feedbackValue.length > 0) {
      const feedbackData = {
        feedback_rating: this.currentRating,
        feedback_text: feedbackValue,
        username: this.username
      };

      try {
        await this.requestFeedbackResponse(feedbackData);
        this.feedbackMessage = `Thank you for your feedback! You rated us ${this.currentRating} star(s).`;
        this.messageType = 'success';
      } catch {
        this.feedbackMessage = 'Apologies! Unable to submit feedback. Please try again later.';
        this.messageType = 'error';
      }

      this.fadeOut = false;
      setTimeout(() => {
        this.fadeOut = true;
      }, 3000);

      this.toggleFeedbackForm();
    } else {
      this.feedbackMessage = 'Please enter your feedback before submitting';
      this.messageType = 'error';
    }

    this.feedbackSubmitted.emit();
  }

  // Updates selected rating when a star is clicked
  public onRatingClick(rating: number): void {
    this.currentRating = rating;
  }

  // Makes the API call to submit feedback data
  private requestFeedbackResponse(feedbackData: { feedback_rating: number; feedback_text: string; username: string }): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.appConfigService.init();
        this.appService.submitGeneralFeedback(feedbackData).subscribe({
          next: () => resolve(),
          error: () => reject(),
          complete: () => {}
        });
      } catch {
        reject();
      }
    });
  }
}
